// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AttachableCharge } from '@/billing/api/get-attachable-charges';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { formatShortDate } from '@/utilities/date-time';
import { formatNumber } from '@/utilities/number';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { omit } from 'lodash-es';
import { HTMLAttributes } from 'react';

interface Props {
  label?: string;
  placeholder?: string;
  value: AttachableCharge | null;
  onChange: (charge: AttachableCharge | null) => void;
  options: AttachableCharge[];
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
}

function isOptionEqualToValue(a: AttachableCharge, b: AttachableCharge) {
  return a.chargeKey === b.chargeKey;
}

function getOptionLabel(option: AttachableCharge) {
  return option.label;
}

// Group by project line item, not by item. Two line items on the same
// project can bind to the same item; merging them would hide line-item
// identity from the picker. lineItemSequence is the project_line_item.sequence
// column, which is already 1-indexed in the database, so it is rendered
// verbatim (no +1 offset).
function groupBy(option: AttachableCharge) {
  return `${option.lineItemSequence}. ${option.itemSku} — ${option.itemName}`;
}

function formatServicePeriod(option: AttachableCharge): string {
  if (option.servicePeriodStart === null || option.servicePeriodEnd === null) {
    return '—';
  }
  return `${formatShortDate(option.servicePeriodStart)} – ${formatShortDate(option.servicePeriodEnd)}`;
}

export function AttachableChargeAutocomplete(props: Props) {
  const renderOption = (
    attributes: HTMLAttributes<HTMLLIElement>,
    option: AttachableCharge
  ) => {
    const liProps = omit(attributes, 'key');
    return (
      <li {...liProps} key={option.chargeKey}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          spacing={2}
        >
          <Stack spacing={0.25} minWidth={0}>
            <Typography variant="body2" noWrap>
              {option.label}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {`${option.status} · qty ${formatNumber(option.quantity)} · ${formatServicePeriod(option)}`}
            </Typography>
          </Stack>
          <Typography variant="body2" fontWeight="bold">
            {formatCurrencyValue(option.totalPrice)}
          </Typography>
        </Stack>
      </li>
    );
  };

  const renderInput = (parameters: AutocompleteRenderInputParams) => (
    <TextField
      label={props.label}
      placeholder={props.placeholder}
      error={props.error}
      helperText={props.helperText}
      {...parameters}
    />
  );

  return (
    <Autocomplete
      value={props.value}
      onChange={(_event, newValue) => props.onChange(newValue)}
      options={props.options}
      disabled={props.disabled}
      size={props.size}
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionLabel={getOptionLabel}
      groupBy={groupBy}
      renderOption={renderOption}
      renderInput={renderInput}
    />
  );
}
